import React from 'react';
import PropTypes from 'prop-types';

import { TextField } from '@andes/textfield';
import { Dropdown, DropdownItem } from '@andes/dropdown';

import { type_range } from '../constants/component';

/**
 * Componente que renderiza un input de rango
 * @param {Object} props - Props del componente
 * @param {string} props.type - Tipo de input (input o dropdown)
 * @param {string} props.name - Nombre del input
 * @param {string} props.id - ID del input
 * @param {string} props.label - Etiqueta del input
 * @param {string} props.pattern - Patrón de validación
 * @param {function} [props.handleInputChange] - Función para manejar cambios en el input
 * @param {Object} [props.handleChangeDropdown] - Funciones para manejar cambios en el dropdown
 * @param {Array} props.values - Valores del dropdown
 * @param {function} [props.setDropdownItemSelected] - Función para establecer el valor seleccionado del dropdown
 * @returns {JSX.Element | null} Input de rango (TextField o Dropdown)
 */

const RangeInputRenderer = ({
  type,
  name,
  id,
  label,
  pattern,
  handleInputChange,
  handleChangeDropdown,
  values,
  setDropdownItemSelected,
}) => {
  switch (type) {
    case type_range.input:
      return (
        <TextField
          data-testid={`${name}-${id}`}
          name={name}
          label=""
          textbox
          placeholder={label}
          pattern={pattern}
          onChange={(e) => handleInputChange?.(e)}
          enterKeyHint="go"
          type="number"
        />
      );
    case type_range.dropdown:
      return (
        <Dropdown
          menuAlignment="bottom"
          size="small"
          label={label}
          onChange={(e, value) => {
            handleChangeDropdown[name](e, value);
            setDropdownItemSelected?.(value);
          }}
          data-testid={`${name}-${id}`}
        >
          {[{ name: label, value: label, disabled: false }, ...values].map(({ name: valueName, disabled }) => (
            <DropdownItem
              key={valueName}
              value={valueName}
              title={valueName}
              disabled={disabled}
              data-testid={`${valueName}-${label}`}
            />
          ))}
        </Dropdown>
      );

    default:
      return null;
  }
};

RangeInputRenderer.propTypes = {
  handleChangeDropdown: PropTypes.shape({}).isRequired,
  handleInputChange: PropTypes.func.isRequired,
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  pattern: PropTypes.string,
  setDropdownItemSelected: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  values: PropTypes.arrayOf(
    PropTypes.shape({
      disabled: PropTypes.bool,
      name: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
};

export default RangeInputRenderer;
